// frontend/src/utils/preferenceUtils.js

const defaultCycles = [];

export const buildPriorityKeyLabel = (order) => `第${order}希望`;
export const buildCycleKeyLabel = (termId) => `第${termId}クール`;

export const createEmptyPreferenceRow = (cycles = defaultCycles) => {
  return (cycles || []).reduce((acc, cycle) => {
    acc[cycle] = '';
    return acc;
  }, {});
};

export const buildInitialPreferences = (count, cycles = defaultCycles) => {
  const preferences = {};
  for (let i = 1; i <= count; i += 1) {
    const key = buildPriorityKeyLabel(i);
    preferences[key] = createEmptyPreferenceRow(cycles);
  }
  return preferences;
};

export const clonePreferences = (preferences = {}, cycles = defaultCycles) => {
  return Object.entries(preferences || {}).reduce((acc, [key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      acc[key] = { ...value };
    } else {
      acc[key] = createEmptyPreferenceRow(cycles);
    }
    return acc;
  }, {});
};

export const ensurePreferenceRow = (preferences, key, cycles = defaultCycles) => {
  if (!preferences[key] || typeof preferences[key] !== 'object' || Array.isArray(preferences[key])) {
    preferences[key] = createEmptyPreferenceRow(cycles);
  } else {
    preferences[key] = { ...preferences[key] };
  }
  return preferences[key];
};

const parsePriorityKey = (key) => {
  if (typeof key !== 'string') {
    return undefined;
  }
  const match = key.match(/\d+/);
  if (!match) {
    return undefined;
  }
  const value = Number.parseInt(match[0], 10);
  return Number.isNaN(value) ? undefined : value;
};

const mapDepartmentCapacitiesById = (departmentCapacities = []) => {
  const map = new Map();
  (departmentCapacities || []).forEach((capacity) => {
    if (capacity && typeof capacity.id !== 'undefined') {
      map.set(capacity.id, capacity);
    }
  });
  return map;
};

const normalizeCyclePriority = (priority, fallbackIndex, cycleCount) => {
  const numeric = Number(priority);
  if (Number.isNaN(numeric)) {
    return fallbackIndex + 1;
  }
  const clamped = Math.trunc(numeric);
  if (clamped < 1) {
    return 1;
  }
  if (clamped > cycleCount) {
    return cycleCount;
  }
  return clamped;
};

export const convertPreferenceSetToGrid = ({
  preferenceSet,
  cycles = defaultCycles,
  preferenceCount,
  departmentCapacities = [],
}) => {
  const resolvedCount = preferenceCount ?? 0;
  const base = buildInitialPreferences(resolvedCount, cycles);
  if (!preferenceSet || !Array.isArray(preferenceSet.items)) {
    return base;
  }

  const capacityMap = mapDepartmentCapacitiesById(departmentCapacities);
  const cycleList = cycles || [];
  const cycleCount = cycleList.length || 1;

  preferenceSet.items.forEach((item) => {
    if (!item || !item.department_term_capacity_id) {
      return;
    }

    const fromPayloadPriority = item.payload && Number.isInteger(item.payload.priority)
      ? item.payload.priority
      : undefined;
    const fallbackPriority = Math.floor(((item.rank || 1) - 1) / cycleCount) + 1;
    const priorityNumber = fromPayloadPriority || fallbackPriority;
    if (!Number.isInteger(priorityNumber)) {
      return;
    }
    if (priorityNumber < 1 || (resolvedCount && priorityNumber > resolvedCount)) {
      return;
    }
    const priorityKey = buildPriorityKeyLabel(priorityNumber);
    const row = ensurePreferenceRow(base, priorityKey, cycleList);

    let cycleName = item.payload && typeof item.payload.cycle === 'string'
      ? item.payload.cycle
      : undefined;
    if (!cycleName || !cycleList.includes(cycleName)) {
      const fallbackIndex = (item.term_id && Number.isInteger(item.term_id))
        ? item.term_id - 1
        : ((item.rank || 1) - 1) % cycleCount;
      cycleName = cycleList[fallbackIndex] || cycleList[0];
    }
    if (!cycleName) {
      return;
    }

    const capacityRecord = capacityMap.get(item.department_term_capacity_id);
    const displayName = capacityRecord?.display_name || item.payload?.display_name || '';
    const termId = item.term_id || capacityRecord?.term_id || (cycleList.indexOf(cycleName) + 1);
    const payloadCyclePriority = item.payload?.cycle_priority;
    const cyclePriority = normalizeCyclePriority(payloadCyclePriority, termId - 1, cycleCount);

    row[cycleName] = {
      id: item.id,
      department_term_capacity_id: item.department_term_capacity_id,
      display_name: displayName,
      capacity: typeof capacityRecord?.capacity !== 'undefined' ? capacityRecord.capacity : null,
      term_id: termId,
      department_id: capacityRecord?.department_id || item.payload?.department_id || null,
      institution_id: capacityRecord?.institution_id || item.payload?.institution_id || null,
      cycle_priority: cyclePriority,
    };
  });

  return base;
};

export const buildPreferencePayloads = ({
  preferences = {},
  cycles = defaultCycles,
  skipFixed = true,
  candidateType = 'STANDARD',
  userId,
  cycleSettings = {},
}) => {
  const unifiedItems = [];
  const legacyPayload = [];
  const cycleList = cycles || [];
  const cycleCount = cycleList.length || 1;

  Object.entries(preferences || {}).forEach(([priorityKey, cycleMap]) => {
    const priorityNumber = parsePriorityKey(priorityKey);
    if (!priorityNumber) {
      return;
    }
    cycleList.forEach((cycleName, index) => {
      const value = cycleMap && cycleMap[cycleName];
      if (!value || !value.department_term_capacity_id) {
        return;
      }
      if (skipFixed && value.isFixed) {
        return;
      }
      const settings = cycleSettings?.[cycleName];
      const cyclePriority = normalizeCyclePriority(settings?.priority, index, cycleCount);
      const termId = value.term_id || index + 1;
      const rank = (priorityNumber - 1) * cycleCount + (index + 1);
      unifiedItems.push({
        rank,
        department_term_capacity_id: value.department_term_capacity_id,
        term_id: termId,
        candidate_type: candidateType,
        payload: {
          priority: priorityNumber,
          cycle: cycleName,
          cycle_priority: cyclePriority,
          display_name: value.display_name,
          department_id: value.department_id,
          institution_id: value.institution_id,
        },
      });
      if (userId) {
        legacyPayload.push({
          department_term_capacity_id: value.department_term_capacity_id,
          preference_order: priorityNumber,
          cycle_priority: cyclePriority,
        });
      }
    });
  });

  unifiedItems.sort((a, b) => a.rank - b.rank);

  const cyclePayload = cycleList.map((cycleName, index) => {
    const settings = cycleSettings?.[cycleName] || {};
    const priority = settings.excluded
      ? null
      : normalizeCyclePriority(settings.priority, index, cycleCount);
    return {
      term_id: index + 1,
      cycle_name: cycleName,
      priority,
      excluded: Boolean(settings.excluded),
    };
  });

  return { unifiedItems, legacyPayload, cyclePayload };
};

export const mergeFixedCells = ({
  currentPreferences = {},
  nextPreferences = {},
  cycles = defaultCycles,
}) => {
  const merged = clonePreferences(nextPreferences, cycles);
  Object.entries(currentPreferences || {}).forEach(([priorityKey, cycleMap]) => {
    Object.entries(cycleMap || {}).forEach(([cycleName, value]) => {
      if (value && value.isFixed) {
        const row = ensurePreferenceRow(merged, priorityKey, cycles);
        row[cycleName] = value;
      }
    });
  });
  return merged;
};


